import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatRp, imageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Design System v2 (Phase 2) — Cart migrated from hardcoded #E5E5E5/#5C5C5C/
// #FAF9F6/white to canonical color tokens:
//   bg-white          → color-surface (via .color-card)
//   #E5E5E5           → var(--color-border)
//   #5C5C5C           → var(--color-text-secondary)
//   #FAF9F6           → var(--color-surface-secondary) (quantity ctrl / thumb bg)
//   #D1D5DB           → var(--color-text-secondary) (icon in empty state)
// Semantic status colors preserved (--danger on remove hover).
export default function CartPage() {
  const { items, updateQty, removeItem, cartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <ShoppingBag className="mx-auto text-[color:var(--color-text-secondary)] opacity-40" size={48} />
        <h2 className="font-display text-2xl font-bold mt-4">Login untuk Melihat Keranjang</h2>
        <p className="text-sm text-[color:var(--color-text-secondary)] mt-2">Anda perlu masuk untuk menyimpan produk di keranjang.</p>
        <Link to="/login"><Button className="mt-5 system-btn-primary rounded-xl">Masuk Sekarang</Button></Link>
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="max-w-md mx-auto py-16 px-4 text-center">
        <ShoppingBag className="mx-auto text-[color:var(--color-text-secondary)] opacity-40" size={48} />
        <h2 className="font-display text-2xl font-bold mt-4">Keranjang Kosong</h2>
        <p className="text-sm text-[color:var(--color-text-secondary)] mt-2">Yuk mulai belanja produk favoritmu!</p>
        <Link to="/katalog"><Button className="mt-5 system-btn-primary rounded-xl">Lihat Produk</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-3xl sm:text-4xl font-black mb-6" data-testid="cart-title">Keranjang Belanja</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-3">
          {items.map((item) => {
            const p = item.product;
            const v = item.variant;
            const isVariant = !!v;
            const price = isVariant && typeof v.effective_price === "number"
              ? v.effective_price
              : (p.sale_price || p.price);
            const stock = isVariant ? Number(v.stock || 0) : Number(p.stock || 0);
            const displayImg = isVariant && v.image ? v.image : (p.images?.[0]);
            const unavailable = item.available === false;
            return (
              <div key={item.id} className={`color-card rounded-2xl p-4 flex gap-3 sm:gap-4 ${unavailable ? "opacity-60" : ""}`} data-testid={`cart-item-${item.id}`}>
                <Link to={`/produk/${p.slug}`} className="shrink-0">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl color-surface-secondary overflow-hidden">
                    {displayImg && <img src={imageUrl(displayImg)} alt={p.name} className="w-full h-full object-cover" />}
                  </div>
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/produk/${p.slug}`} className="font-semibold text-sm sm:text-base line-clamp-2 hover:underline">
                    {p.name}
                  </Link>
                  {isVariant && (
                    <div className="mt-0.5 text-xs font-semibold text-[color:var(--color-text-primary)]" data-testid={`cart-item-variant-${item.id}`}>
                      {v.label}
                      {v.sku && <span className="ml-2 text-[10px] font-normal text-[color:var(--color-text-secondary)]"> · SKU: {v.sku}</span>}
                    </div>
                  )}
                  <div className="text-xs text-[color:var(--color-text-secondary)] mt-1">Stok: {stock} {p.unit}</div>
                  {unavailable && (
                    <div className="text-xs text-[color:var(--danger)] font-semibold mt-1" data-testid={`cart-item-unavailable-${item.id}`}>
                      {item.unavailable_reason === "variant_required"
                        ? "Produk kini memiliki varian — hapus & pilih ulang."
                        : (item.unavailable_reason === "variant_missing"
                            ? "Varian sudah tidak tersedia."
                            : "Item tidak tersedia untuk jumlah ini.")}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                    <div className="commerce-price font-bold">{formatRp(price)}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center color-surface-secondary border color-border rounded-lg">
                        <button onClick={() => updateQty(item.id, item.product_id, Math.max(1, item.quantity - 1))} className="w-8 h-8 grid place-items-center">
                          <Minus size={12} />
                        </button>
                        <div className="w-8 text-center text-sm font-bold">{item.quantity}</div>
                        <button onClick={() => updateQty(item.id, item.product_id, Math.min(stock || 1, item.quantity + 1))} className="w-8 h-8 grid place-items-center" disabled={unavailable}>
                          <Plus size={12} />
                        </button>
                      </div>
                      <button onClick={() => removeItem(item.id)} data-testid={`remove-item-${item.id}`} className="text-[color:var(--color-text-secondary)] hover:text-[color:var(--danger)] p-1">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <aside className="color-card rounded-2xl p-5 h-fit sticky top-24">
          <h3 className="font-display text-xl font-bold mb-4">Ringkasan</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-[color:var(--color-text-secondary)]">Subtotal</span><span className="font-semibold">{formatRp(cartTotal)}</span></div>
            <div className="flex justify-between text-xs text-[color:var(--color-text-secondary)]"><span>Ongkir</span><span>Dihitung saat checkout</span></div>
          </div>
          <div className="border-t color-border my-4" />
          <div className="flex justify-between items-baseline">
            <span className="font-semibold">Total</span>
            <span className="font-display font-black text-2xl commerce-price" data-testid="cart-total">{formatRp(cartTotal)}</span>
          </div>
          {/* Design System v2 — Primary CTA (checkout) uses color-primary +
              hover color-primary-dark. Class also lives on Auth "Masuk" and
              header login for consistency. */}
          <Button
            onClick={() => navigate("/checkout")}
            className="mt-4 w-full h-12 color-btn-primary rounded-xl font-bold"
            data-testid="checkout-btn"
          >
            Lanjut Checkout
          </Button>
          <Link to="/katalog" className="block text-center text-sm text-[color:var(--color-text-secondary)] hover:underline mt-3">Lanjut Belanja</Link>
        </aside>
      </div>
    </div>
  );
}
